import { AllianceStatus, UserAlliance } from './alliance';
import { JobClient, JobStatus } from './common';
import { City } from './profile';
import { ServiceCategory } from './service';
import { CreditActionType } from './credits';

export interface BudgetRange {
  id: number;
  code: string;
  min_amount: number | null;
  max_amount: number | null;
}

export type QuotePricingType = 'hourly' | 'flat_rate' | 'unit' | 'fee' | 'adjustment' | 'tax';

export interface QuoteTask {
  id: string;
  name: string;
  description?: string;
  pricing_type: QuotePricingType;
  quantity: number;
  unit_price: number;
  total_amount: number;
  sort_order: number;
}

export interface Freelancer {
  id: string;
  first_name: string;
  last_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  tasks: QuoteTask[];
  services: ServiceCategory[];
}

export interface FreelancersWithPrincipal {
  job_id: string;
  job_title: string | null;
  total_amount_sum: number | null;
  principal_freelancer: Freelancer | null;
  freelancers: Freelancer[];
}

export interface QuoteTaskInput {
  id?: string;
  name: string;
  pricing_type: QuotePricingType;
  quantity: number;
  unit_price: number;
}

export interface ChildQuote {
  id: string;
  status: string;
  submitted_at: string | null;
  total_amount: number | null;
  tasks: Array<{
    id: string;
    name: string;
    description?: string;
    pricing_type: QuotePricingType;
    quantity: number;
    unit_price: number;
    total_amount: number;
    sort_order: number;
  }>;
  quote_owner: QuoteOwner;
}

export interface QuoteAllianceFreelancer {
  profile_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
}

export interface UserAllianceWithQuote {
  userAlliance: UserAlliance;
  quoteAlliance?: QuoteAlliance; // Optional - undefined for not-yet-invited alliances
}

export interface QuoteAlliance {
  id: string;
  invited_freelancer: QuoteAllianceFreelancer;
  service_category: ServiceCategory;
  status: AllianceStatus;
  created_at: string;
  updated_at: string;
  child_quote?: ChildQuote | null;
}

export interface QuoteJob {
  id: string;
  title: string;
  description: string;
  published_at: string | null;
  status: JobStatus;
  start_date: string | null;
  end_date: string | null;
  completed_at: string | null;
  visibility: 'public' | 'private';
  city: City;
  freelancer?: { id: string; first_name: string; avatar_url: string };
  budget_range: BudgetRange;
  on_site: boolean;
  languages: string[];
  required_services: ServiceCategory[];
  client: JobClient;
}

export interface QuoteOwner {
  id: string;
  first_name: string;
  last_name: string;
  bio?: string;
  avatar_url?: string | null;
  services?: ServiceCategory[];
  role?: string;
}

export interface Quote {
  id: string;
  job_id: string;
  parent_quote_id: string | null;
  parent_quote_status: string | null;
  status: string;
  message: string | null;
  change_request_message?: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  tasks: QuoteTask[];
  alliances: QuoteAlliance[];
  job: QuoteJob;
  quote_alliance_status: string | null;
  parent_quote_owner: QuoteOwner | null;
  quote_owner: QuoteOwner;
}

export interface QuoteEstimate {
  freelancer_id: string;
  hours: number;
  hourly_rate: number;
}

export interface QuoteRequest {
  id: string;
  job: QuoteJob;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  parent_quote_id: string | null;
  created_at: string;
  expires_at: string;
  has_quote: boolean;
  request_message?: string | null;
}

export interface AllianceQuoteRequest {
  id: string;
  job: QuoteJob;
  parent_quote_owner: QuoteOwner;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  parent_quote_id: string | null;
  created_at: string;
  has_quote: boolean;
  request_message?: string | null;
}

export interface RawQuoteTask {
  id: string;
  name: string;
  description?: string | null;
  pricing_type: QuotePricingType;
  quantity: number;
  unit_price: number;
  total_amount: number | null;
  sort_order: number | null;
}

export interface RawQuoteAlliance {
  id: string;
  invited_freelancer_id: string;
  invited_freelancer_first_name: string;
  invited_freelancer_last_name: string;
  invited_freelancer_avatar_url: string;
  invited_freelancer_service_category_id: number;
  invited_freelancer_service_category_slug: string;
  invited_freelancer_service_category_label: string;
  status: AllianceStatus;
  created_at: string;
  updated_at: string;
  child_quote: {
    id: string;
    status: string;
    submitted_at: string | null;
    total_amount: number | null;
    tasks: Array<{
      id: string;
      name: string;
      description?: string | null;
      pricing_type: QuotePricingType;
      quantity: number;
      unit_price: number;
      total_amount: number;
      sort_order: number;
    }>;
    quote_owner_id: string;
    quote_owner_first_name: string;
    quote_owner_last_name: string;
    quote_owner_avatar_url: string;
    quote_owner_services: ServiceCategory[];
    quote_owner_bio: string | null;
  } | null;
}

export interface RawQuote {
  id: string;
  job_id: string;
  parent_quote_id: string | null;
  parent_quote_status: string | null;
  parent_quote_owner_id: string;
  parent_quote_owner_first_name: string;
  parent_quote_owner_last_name: string;
  parent_quote_owner_avatar_url: string | null;
  status: string;
  message: string | null;
  change_request_message?: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  tasks: RawQuoteTask[];
  alliances: RawQuoteAlliance[];
  job_title: string;
  job_description: string;
  job_status: JobStatus;
  job_published_at: string;
  job_start_date: string | null;
  job_end_date: string | null;
  job_on_site: boolean;
  job_languages: string[];
  job_visibility: string;
  job_created_at: string;
  job_completed_at: string | null;
  client_id: string;
  client_first_name: string;
  client_last_name: string;
  client_company_name: string | null;
  client_avatar_url: string | null;
  city_id: number;
  city_name: string;
  budget_range_id: number;
  budget_range_code: string;
  budget_range_min_amount: number;
  budget_range_max_amount: number;
  listing_type_id: number;
  listing_type_credit_action_type: CreditActionType;
  listing_type_code: string;
  required_services: Array<{
    id: number;
    slug: string;
    label: string;
  }>;
  quote_alliance_status: string | null;
  quote_owner_id: string;
  quote_owner_first_name: string;
  quote_owner_last_name: string;
  quote_owner_avatar_url: string | null;
  quote_owner_bio: string | null;
  quote_owner_services: Array<{
    id: number;
    slug: string;
    label: string;
  }>;
  quote_request_status?: string;
}

export function mapUserQuoteTaskToQuoteTask(userQuoteTask: RawQuoteTask): QuoteTask {
  return {
    id: userQuoteTask.id,
    name: userQuoteTask.name,
    description: userQuoteTask.description ?? undefined,
    pricing_type: userQuoteTask.pricing_type,
    quantity: userQuoteTask.quantity ?? 0,
    unit_price: userQuoteTask.unit_price ?? 0,
    total_amount: userQuoteTask.total_amount ?? 0,
    sort_order: userQuoteTask.sort_order ?? 0,
  };
}

export function mapRawQuoteAllianceToQuoteAlliance(alliance: RawQuoteAlliance): QuoteAlliance {
  return {
    id: alliance.id,
    invited_freelancer: {
      profile_id: alliance.invited_freelancer_id,
      first_name: alliance.invited_freelancer_first_name,
      last_name: alliance.invited_freelancer_last_name,
      avatar_url: alliance.invited_freelancer_avatar_url,
    },
    service_category: {
      id: alliance.invited_freelancer_service_category_id,
      slug: alliance.invited_freelancer_service_category_slug,
      label: alliance.invited_freelancer_service_category_label,
    },
    status: alliance.status,
    created_at: alliance.created_at,
    updated_at: alliance.updated_at,
    child_quote: alliance.child_quote
      ? {
          id: alliance.child_quote.id,
          status: alliance.child_quote.status,
          submitted_at: alliance.child_quote.submitted_at,
          total_amount: alliance.child_quote.total_amount,
          tasks: alliance.child_quote.tasks.map(task => ({
            id: task.id,
            name: task.name,
            description: task.description ?? undefined,
            pricing_type: task.pricing_type,
            quantity: task.quantity,
            unit_price: task.unit_price,
            total_amount: task.total_amount,
            sort_order: task.sort_order,
          })),
          quote_owner: {
            id: alliance.child_quote.quote_owner_id,
            first_name: alliance.child_quote.quote_owner_first_name,
            last_name: alliance.child_quote.quote_owner_last_name,
            avatar_url: alliance.child_quote.quote_owner_avatar_url,
            services: alliance.child_quote.quote_owner_services,
            bio: alliance.child_quote.quote_owner_bio ?? undefined,
          },
        }
      : null,
  };
}

export function transformRawQuoteToQuote(userQuote: RawQuote): Quote {
  const job: QuoteJob = {
    id: userQuote.job_id,
    title: userQuote.job_title,
    description: userQuote.job_description,
    published_at: userQuote.job_published_at,
    status: userQuote.job_status,
    start_date: userQuote.job_start_date!,
    end_date: userQuote.job_end_date!,
    completed_at: userQuote.job_completed_at!,
    visibility: userQuote.job_visibility as 'public' | 'private',
    city: { id: userQuote.city_id, name: userQuote.city_name },
    budget_range: {
      id: userQuote.budget_range_id,
      code: userQuote.budget_range_code,
      min_amount: userQuote.budget_range_min_amount,
      max_amount: userQuote.budget_range_max_amount,
    },
    on_site: userQuote.job_on_site,
    languages: userQuote.job_languages || [],
    required_services: userQuote.required_services,
    client: {
      id: userQuote.client_id,
      first_name: userQuote.client_first_name,
      last_name: userQuote.client_last_name,
      company_name: userQuote.client_company_name! ?? '',
      avatar_url: userQuote.client_avatar_url! ?? '',
    },
  };

  const parent_quote_owner: QuoteOwner | null = userQuote.parent_quote_owner_id
    ? {
        id: userQuote.parent_quote_owner_id,
        first_name: userQuote.parent_quote_owner_first_name,
        last_name: userQuote.parent_quote_owner_last_name,
        avatar_url: userQuote.parent_quote_owner_avatar_url ?? undefined,
      }
    : null;

  const quote_owner: QuoteOwner = {
    id: userQuote.quote_owner_id,
    first_name: userQuote.quote_owner_first_name,
    last_name: userQuote.quote_owner_last_name,
    avatar_url: userQuote.quote_owner_avatar_url ?? undefined,
    bio: userQuote.quote_owner_bio ?? undefined,
    services: userQuote.quote_owner_services,
  };

  const tasks: QuoteTask[] = userQuote.tasks.map(mapUserQuoteTaskToQuoteTask);

  // Transform alliances to QuoteAlliance format
  const alliances: QuoteAlliance[] = userQuote.alliances.map(mapRawQuoteAllianceToQuoteAlliance);

  return {
    id: userQuote.id,
    job_id: userQuote.job_id,
    parent_quote_id: userQuote.parent_quote_id,
    status: userQuote.status,
    message: userQuote.message,
    change_request_message: userQuote.change_request_message ?? null,
    created_at: userQuote.created_at,
    updated_at: userQuote.updated_at,
    submitted_at: userQuote.submitted_at,
    parent_quote_status: userQuote.parent_quote_status,
    quote_alliance_status: userQuote.quote_alliance_status,
    tasks,
    alliances,
    job,
    parent_quote_owner,
    quote_owner,
  };
}

export interface RawQuoteRequest {
  id: string;
  job_id: string;
  target_freelancer_id: string;
  client_id: string;
  status: string;
  reject_reason: string | null;
  created_at: string;
  updated_at: string;
  job_title: string;
  job_description: string;
  job_published_at: string;
  job_status: string;
  job_start_date: string | null;
  job_end_date: string | null;
  job_on_site: boolean;
  job_languages: string[];
  job_visibility: string;
  client_first_name: string;
  client_last_name: string;
  client_company_name: string | null;
  client_avatar_url: string | null;
  city_id: number;
  city_name: string;
  budget_range_id: number;
  budget_range_code: string;
  budget_range_min_amount: number;
  budget_range_max_amount: number;
  required_services: Array<{
    id: number;
    slug: string;
    label: string;
  }>;
  has_quote: boolean;
}

export interface UserAllianceQuoteRequest {
  id: string;
  job_id: string;
  invited_freelancer_id: string;
  quote_owner_id: string;
  parent_quote_id: string | null;
  status: string;
  reject_reason: string | null;
  created_at: string;
  updated_at: string;
  job_title: string;
  job_description: string;
  job_status: string;
  job_published_at: string;
  job_start_date: string | null;
  job_end_date: string | null;
  job_on_site: boolean;
  job_languages: string[];
  job_visibility: string;
  parent_quote_owner_id: string;
  parent_quote_owner_first_name: string;
  parent_quote_owner_last_name: string;
  parent_quote_owner_avatar_url: string | null;
  client_id: string;
  client_first_name: string;
  client_last_name: string;
  client_company_name: string | null;
  client_avatar_url: string | null;
  city_id: number;
  city_name: string;
  budget_range_id: number;
  budget_range_code: string;
  budget_range_min_amount: number;
  budget_range_max_amount: number;
  required_services: Array<{
    id: number;
    slug: string;
    label: string;
  }>;
  has_quote: boolean;
  request_message?: string | null;
}
